import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRole, ROLES } from "../context/RoleContext";
import { AdminLoginDialog } from "../components/AdminLoginDialog";
import { ThemeToggle } from "../components/ThemeToggle";
import {
  ArrowRight, Plane, Users, LogIn,
  ShieldCheck, Eye, FileLock2, Headphones, Smartphone, Clock, MessageCircleMore, Route,
} from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();
  const { setRole } = useRole();
  const [loginOpen, setLoginOpen] = useState(false);

  const choose = (r) => {
    setRole(r);
    navigate(r === ROLES.ADMIN ? "/admin" : "/traveler/submit");
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="landing-page">
      <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />

      {/* Hero imagery backdrop */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-0 right-0 w-[60%] h-[80%] opacity-25 blur-[2px]"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1585975754487-53b23cff1a9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "linear-gradient(to left, black 20%, transparent 80%)",
            WebkitMaskImage: "linear-gradient(to left, black 20%, transparent 80%)",
          }}
        />
      </div>

      <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-12 sm:mb-16">
          <div className="flex items-center gap-3">
            <img
              src="/carrey-logo.png"
              alt="Carrey Transportation · Monterrey, México"
              className="h-16 sm:h-20 w-auto drop-shadow-[0_0_24px_rgba(240,125,0,0.3)]"
              data-testid="brand-logo"
            />
          </div>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button
              onClick={() => setLoginOpen(true)}
              data-testid="top-menu-admin-login"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold text-white bg-[#F07D00]/20 border border-[#F07D00]/40 hover:bg-[#F07D00]/30 hover:border-[#F07D00] shadow-[0_0_16px_rgba(240,125,0,0.15)] transition-all"
            >
              <LogIn className="h-3.5 w-3.5" /> Admin Login
            </button>
          </div>
        </div>

        {/* Hero */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#F07D00]/30 bg-[#F07D00]/10 mb-8">
              <span className="h-2 w-2 rounded-full bg-[#F07D00] animate-slow-pulse" />
              <span className="text-xs font-semibold tracking-wider uppercase text-[#F07D00]">Monterrey · Apodaca · México · NL</span>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-white mb-6">
              Travel safely.
              <span className="block mt-2">
                <span className="bg-gradient-to-r from-[#F07D00] via-[#FF8C1A] to-[#FFB366] bg-clip-text text-transparent">Arrive on time.</span>
              </span>
            </h1>

            <p className="text-base sm:text-lg text-zinc-400 leading-relaxed max-w-xl mb-10">
              Submit your trip details once and let Carrey Transportation's coordinators handle the rest — vetted drivers, confirmed lodging, and a single point of contact from arrival to departure.
            </p>

            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10">
                <ShieldCheck className="h-4 w-4 text-emerald-400" />
                <span className="text-xs font-medium text-emerald-100">Vetted licensed drivers</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#F07D00]/30 bg-[#F07D00]/10">
                <Clock className="h-4 w-4 text-[#F07D00]" />
                <span className="text-xs font-medium text-orange-100">24/7 coordination</span>
              </div>
            </div>
          </div>

          {/* Single Booking card */}
          <div className="animate-fade-up" style={{ animationDelay: "0.1s" }}>
            <p className="label-eyebrow mb-5">Book your trip</p>

            <button
              onClick={() => choose(ROLES.TRAVELER)}
              data-testid="role-traveler-button"
              className="glass-card glass-card-hover w-full text-left p-6 sm:p-7 group"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="h-2 w-2 rounded-full bg-cyan-400" />
                    <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-cyan-400">Traveler</span>
                  </div>
                  <h3 className="font-heading text-xl sm:text-2xl font-semibold text-white mb-2">Submit travel details</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">Share your flight, lodging, and expected work locations. Our coordinators confirm everything and assign your driver before you land.</p>
                </div>
                <ArrowRight className="h-5 w-5 text-zinc-500 group-hover:text-white group-hover:translate-x-1 transition-all flex-shrink-0" />
              </div>
            </button>

            <p className="text-xs text-zinc-500 pt-4">Takes under 2 minutes · No account required.</p>
          </div>
        </div>

        {/* SAFETY */}
        <section className="mt-24 lg:mt-32" data-testid="safety-section">
          <div className="flex items-center gap-2 mb-6">
            <div className="h-7 w-7 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
            </div>
            <p className="label-eyebrow text-emerald-400">Safety first</p>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-white mb-3 max-w-2xl">
            Every kilometer, accounted for.
          </h2>
          <p className="text-sm sm:text-base text-zinc-400 leading-relaxed max-w-2xl mb-10">
            Travel coordination at Carrey is designed around traveler safety — from the moment your trip is submitted to the moment you depart. We don't hand you off; we walk with you.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FeatureCard
              icon={ShieldCheck}
              accent="emerald"
              title="Vetted, licensed drivers"
              body="Every assigned driver is background-checked and operates a registered Carrey vehicle with verified license plates."
              testid="safety-card-drivers"
            />
            <FeatureCard
              icon={Eye}
              accent="emerald"
              title="Real-time visibility"
              body="Coordinators track who's currently in town, their lodging, expected locations, and arrival/departure status — across Monterrey and Apodaca."
              testid="safety-card-visibility"
            />
            <FeatureCard
              icon={FileLock2}
              accent="emerald"
              title="Full audit trail"
              body="Every status change, message, and driver assignment is timestamped and logged. Enterprise-grade governance for your trip records."
              testid="safety-card-audit"
            />
          </div>
        </section>

        {/* CONVENIENCE */}
        <section className="mt-20 lg:mt-28" data-testid="convenience-section">
          <div className="flex items-center gap-2 mb-6">
            <div className="h-7 w-7 rounded-md bg-[#F07D00]/15 border border-[#F07D00]/30 flex items-center justify-center">
              <Headphones className="h-3.5 w-3.5 text-[#F07D00]" />
            </div>
            <p className="label-eyebrow text-[#F07D00]">Built for the way you travel</p>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-white mb-3 max-w-2xl">
            Convenience at every touchpoint.
          </h2>
          <p className="text-sm sm:text-base text-zinc-400 leading-relaxed max-w-2xl mb-10">
            From a 90-second submission on your phone to direct WhatsApp contact with your coordinator — Carrey is engineered to remove every friction point in business travel.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <FeatureCard
              icon={Smartphone}
              accent="orange"
              title="Mobile-first booking"
              body="Submit your trip from a phone in under 2 minutes. Multi-step form remembers your progress."
              testid="convenience-card-mobile"
              size="sm"
            />
            <FeatureCard
              icon={MessageCircleMore}
              accent="orange"
              title="WhatsApp ready"
              body="Reach your coordinator directly on WhatsApp the moment your trip is submitted."
              testid="convenience-card-whatsapp"
              size="sm"
            />
            <FeatureCard
              icon={Route}
              accent="orange"
              title="Door-to-door"
              body="From MTY airport to your hotel and on to your work site — your assigned driver handles the route."
              testid="convenience-card-route"
              size="sm"
            />
            <FeatureCard
              icon={Clock}
              accent="orange"
              title="24/7 coordinator"
              body="Plans change. Your coordinator is on call, with templated quick-replies for common updates."
              testid="convenience-card-247"
              size="sm"
            />
          </div>
        </section>

        {/* Stat strip */}
        <div className="mt-20 lg:mt-28 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10 backdrop-blur-xl">
          {[
            { k: "2", label: "Hubs in NL" },
            { k: "9", label: "Status states" },
            { k: "24/7", label: "Coordination" },
            { k: "100%", label: "Audit trail" },
          ].map((s) => (
            <div key={s.label} className="bg-[#0B0C10]/80 p-6">
              <div className="font-heading text-3xl font-semibold text-white tracking-tight">{s.k}</div>
              <div className="label-eyebrow mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center text-xs text-zinc-600">
          © {new Date().getFullYear()} Carrey Transportation · Monterrey · Apodaca · Nuevo León, México
        </div>
      </div>

      <AdminLoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
    </div>
  );
}

const ACCENT_STYLES = {
  emerald: { ring: "border-emerald-500/20 hover:border-emerald-500/40", icon: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  orange: { ring: "border-[#F07D00]/20 hover:border-[#F07D00]/40", icon: "bg-[#F07D00]/10 text-[#F07D00] border-[#F07D00]/30" },
};

function FeatureCard({ icon: Icon, accent = "orange", title, body, testid, size = "md" }) {
  const a = ACCENT_STYLES[accent];
  return (
    <div data-testid={testid} className={`glass-card p-5 sm:p-6 transition-all hover:-translate-y-0.5 ${a.ring}`}>
      <div className={`h-9 w-9 rounded-lg border flex items-center justify-center mb-4 ${a.icon}`}>
        <Icon className="h-4 w-4" />
      </div>
      <h3 className={`font-heading font-semibold text-white mb-2 ${size === "sm" ? "text-base" : "text-lg"}`}>{title}</h3>
      <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}
